import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Footer } from "../components/footer/footer";
import { Scroll } from "../components/scroll/scroll";
import { Header } from "../components/header/header";
import { AgentInfo } from "../components/agent-info/agent-info";
import { useI18n } from "../i18n";
import "./qrcode.scss";

type ExportFormat = "png" | "svg";
type ExportSize = "small" | "medium" | "large";

const SIZES: Record<ExportSize, number> = {
  small: 320,
  medium: 640,
  large: 960,
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadSvg(svgEl: SVGSVGElement, size: number) {
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("width", String(size));
  clone.setAttribute("height", String(size));
  const data = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([data], { type: "image/svg+xml" });
  downloadBlob(blob, `qrcode-${size}w.svg`);
}

function downloadPng(svgEl: SVGSVGElement, size: number) {
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("width", String(size));
  clone.setAttribute("height", String(size));
  const data = new XMLSerializer().serializeToString(clone);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, size, size);
    canvas.toBlob((blob) => {
      if (blob) downloadBlob(blob, `qrcode-${size}w.png`);
    }, "image/png");
  };
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
}

export default function QRCode() {
  const { translations } = useI18n();
  const [value, setValue] = useState("");
  const [activeExport, setActiveExport] = useState<ExportFormat | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const handleExport = (format: ExportFormat) => {
    setActiveExport(activeExport === format ? null : format);
  };

  const handleDownload = (format: ExportFormat, size: ExportSize) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    if (format === "svg") {
      downloadSvg(svgEl, SIZES[size]);
    } else {
      downloadPng(svgEl, SIZES[size]);
    }
    setActiveExport(null);
  };

  return (
    <Scroll>
      <main className="page">
        <Header
          title={translations.tools.qrCodePage.title}
          breadcrumbs={[
            { label: "GAraujo", href: "/" },
            { label: translations.tools.nav, href: "/tools" },
            { label: translations.tools.qrCodePage.title, href: "/tools/qrcode" },
          ]}
        />
        <div className="content">
          <section className="qrcode-tool">
            <div className="qrcode-tool__controls">
              <label className="qrcode-tool__field">
                <span className="qrcode-tool__label">{translations.tools.qrCodePage.valueLabel}</span>
                <input
                  className="qrcode-tool__input"
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={translations.tools.qrCodePage.valuePlaceholder}
                />
              </label>
            </div>

            <div className="qrcode-tool__preview">
              {value ? (
                <QRCodeSVG ref={svgRef} value={value} size={200} />
              ) : (
                <p className="qrcode-tool__empty">{translations.tools.qrCodePage.emptyMessage}</p>
              )}
            </div>

            {value && (
              <div className="qrcode-tool__exports">
                <button
                  className={`qrcode-tool__export-btn ${activeExport === "png" ? "qrcode-tool__export-btn--active" : ""}`}
                  onClick={() => handleExport("png")}
                >
                  PNG
                </button>
                <button
                  className={`qrcode-tool__export-btn ${activeExport === "svg" ? "qrcode-tool__export-btn--active" : ""}`}
                  onClick={() => handleExport("svg")}
                >
                  SVG
                </button>

                {activeExport && (
                  <div className="qrcode-tool__sizes">
                    {(["small", "medium", "large"] as ExportSize[]).map((s) => (
                      <button
                        key={s}
                        className="qrcode-tool__size-btn"
                        onClick={() => handleDownload(activeExport, s)}
                      >
                        {translations.tools.qrCodePage[`size${s.charAt(0).toUpperCase() + s.slice(1)}` as keyof typeof translations.tools.qrCodePage]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          <AgentInfo
            toolUrl="/tools/qrcode"
            toolName="generate_qr_code"
            description={translations.tools.qrCodeDesc}
            steps={[
              translations.tools.qrCodePage.agentStep1,
              translations.tools.qrCodePage.agentStep2,
              translations.tools.qrCodePage.agentStep3,
            ]}
          />
        </div>
        <Footer />
      </main>
    </Scroll>
  );
}
